import {useNotifications} from '@/hooks/useInitializeNotifications';
import {Close, NotificationsActive} from '@mui/icons-material';
import {tokens} from '@ark/web';
import {Box, Button, IconButton, Typography} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {useCallback, useState} from 'react';

const DISMISSED_KEY = 'ark_notif_permission_dismissed';

export const NotificationPermissionBanner = () => {
  const theme = useTheme();
  const {status, subscribe} = useNotifications();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === 'true',
  );

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    localStorage.setItem(DISMISSED_KEY, 'true');
  }, []);

  const handleEnable = useCallback(async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        await subscribe();
        handleDismiss();
      }
    } catch {
      handleDismiss();
    }
  }, [subscribe, handleDismiss]);

  if (status !== 'default' || dismissed) return null;

  return (
    <Box
      sx={{
        mx: 2,
        mb: 2,
        p: 2,
        borderRadius: `${tokens.radius.lg}px`,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <NotificationsActive
        sx={{fontSize: 24, color: theme.palette.warning.main, flexShrink: 0}}
      />
      <Box sx={{flex: 1}}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.3,
            color: theme.palette.text.primary,
          }}
        >
          Stay in the loop
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            lineHeight: 1.3,
            color: theme.palette.text.secondary,
          }}
        >
          Enable push notifications for real-time updates
        </Typography>
      </Box>
      <Button
        onClick={handleEnable}
        data-attr="notification-permission-banner-enable"
        variant="contained"
        size="small"
        color="primary"
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          fontSize: 13,
          borderRadius: '8px',
          flexShrink: 0,
        }}
      >
        Enable
      </Button>
      <IconButton
        onClick={handleDismiss}
        data-attr="notification-permission-banner-dismiss"
        size="small"
        sx={{color: theme.palette.text.secondary, p: 0.25}}
      >
        <Close sx={{fontSize: 18}} />
      </IconButton>
    </Box>
  );
};
